import { FC, useEffect, useState, useMemo, useRef, useReducer, useCallback } from 'react'
import { useHistory, useLocation } from 'react-router-dom';
import styles from './app-edit-price-fields.module.scss';
import { serviceAreas } from '../../services/service-areas';
import { useTranslation } from 'react-i18next';
import Field from '../../components/shared/Field/Field';
import Modal from '../../components/shared/Modal/Modal';
import BtButton from '../bt-button/bt-button';
import { NotificationManager } from 'react-notifications';
import { TrashIcon } from '../../components/svgs';
import { ReactComponent as EditIcon } from '../../assets/svg/editIcon.svg';
import Selector from '../../components/shared/Selector/Selector';
import TooltipContainer from '../../components/shared/TooltipContainer/TooltipContainer';
import appState from '../../state/AppStateContainer';
import DataTable from '../../components/shared/DataTable/DataTable';
import SearchBarWithActions from '../../components/shared/SearchBarWithActions/SearchBarWithActions';

type Area = {
  name: string;
  _id: string;
  isActive: boolean;
  accountNumber?: string;
  slug?: string;
}

type CategoryItem = {
  label: string;
  value: string; // value is the id of the category/area
  accountNumber?: string;
  linkedCategory?: string[]; // Array of linked super admin category names
  slug?: string;
}

type LocationState = {
  addNewAreaModal?: boolean;
}

type EditDraft = {
  name: string;
  accountNumber: string;
  linkedCategory: CategoryItem[];
}

const AddAreaForm: FC<{
  categories: CategoryItem[];
  superAdminCategories: CategoryItem[];
  loading: boolean;
  isSuperAdminOrganization: boolean;
  onSave: (data: { name: string; accountNumber: string; linkedCategory: CategoryItem[] }) => Promise<void>;
  onClose: () => void;
}> = ({ categories, superAdminCategories, loading, isSuperAdminOrganization, onSave, onClose }) => {
  const [name, setName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [linkedCategory, setLinkedCategory] = useState<CategoryItem[]>([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [t] = useTranslation('settings', { keyPrefix: 'editCategories' });
  const [t2] = useTranslation('supplierDatabase');

  const categoryAlreadyExists = useMemo(() => {
    if (!name.trim()) return false;
    return categories.some(
      cat => cat.label.trim().toLowerCase() === name.trim().toLowerCase()
    ) 
  }, [name, categories, superAdminCategories]);

  const handleSubmit = async () => {
    if (!name.trim() || categoryAlreadyExists) return;
    setSaving(true);

    try {
      await onSave({ name: name.trim(), accountNumber: accountNumber.trim(), linkedCategory });
      setSaved(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', padding: '8px 0' }}>
      <Field
        label={t('edit.categoryNameLabel', 'Kategorinamn')}
        value={name}
        onChange={(value) => setName(value)}
        placeholder={t2('addArea.placeholder.areaName', 'Lägg till yrkeskategori')}
        required
        errorMessage={
          categoryAlreadyExists && !saving && !saved
            ? t2('addArea.tooltip.areaAlreadyExists', 'Kategori finns redan')
            : undefined
        }
      />
      <Field
        label={t2('addArea.label.accountNumber', 'Kontonummer')}
        value={accountNumber}
        onChange={(value) => setAccountNumber(value)}
        placeholder={t('edit.accountNumberPlaceholder', 'T.ex. 1234')}
      />
      {!isSuperAdminOrganization && (
        <Selector
          isDisabled={loading}
          label={t('edit.linkToSuperAdminCategory', 'Länka till global kategori')}
          placeholder={t('edit.selectPlaceholder', 'Välj')}
          isMulti={true}
          options={superAdminCategories}
          onChange={(selected) => setLinkedCategory(selected as CategoryItem[])}
          value={linkedCategory}
        />
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '8px' }}>
        <BtButton color="white" onClick={onClose} disabled={saving}>
          {t('edit.cancel', 'Avbryt')}
        </BtButton>
        <BtButton
          color="primary"
          onClick={handleSubmit}
          disabled={!name.trim() || categoryAlreadyExists || saving}
          loaderShow={saving}
        >
          {saving ? t2('addArea.button.saving', 'Sparar...') : t2('addArea.button.save', 'Spara yrkeskategori')}
        </BtButton>
      </div>
    </div>
  );
};

const AppEditCategories: FC<{
  addNewAreaModal: boolean;
  setAddNewAreaModal: (show: boolean) => void;
}> = ({ addNewAreaModal, setAddNewAreaModal }) => {
  const location = useLocation<LocationState>();
  const history = useHistory();
  const [categories, setCategories] = useState<CategoryItem[]>([]);
  const [superAdminCategories, setSuperAdminCategories] = useState<CategoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [t] = useTranslation('settings', { keyPrefix: 'editCategories'});
  const [t2] = useTranslation('supplierDatabase');
  const [searchTerm, setSearchTerm] = useState('');
  const [addModalKey, setAddModalKey] = useState(0);
  const [editingRowId, setEditingRowId] = useState<string | null>(null); // CategoryItem.value
  const [savingRowId, setSavingRowId] = useState<string | null>(null);
  const editDraftRef = useRef<EditDraft | null>(null);
  const [, forceDraftRender] = useReducer((x: number) => x + 1, 0);
  const nameErrorRef = useRef('');
  const nameCheckTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    (async () => {
      const [allAreas, allSuperAdminAreas] = await Promise.all([
        serviceAreas.getAreas(),
        serviceAreas.fetchSuperAdminAreas()
      ]);

      let mappedCategories = (allAreas ?? []).map((x: Area) => ({
        label: x.name,
        value: x._id,
        accountNumber: x.accountNumber,
        linkedCategory: [] as string[],
        slug: x.slug
      })) as CategoryItem[];


      allSuperAdminAreas?.forEach((item: any) => {
        item.customerAreas?.forEach((x: any) => {
          const categoryIndex = mappedCategories.findIndex(c => c.value === x.areaId);
          if (categoryIndex !== -1) {
            if (!mappedCategories[categoryIndex].linkedCategory) {
              mappedCategories[categoryIndex].linkedCategory = [];
            }
            mappedCategories[categoryIndex].linkedCategory!.push(item.name);
          }
        });
      });

      setCategories(mappedCategories);

      setSuperAdminCategories(allSuperAdminAreas?.map((x: Area) => ({
        label: x.name,
        value: x._id,
      })) ?? []);

      setLoading(false);
    })();

  }, []);

  const updateCategoryLinks = useCallback(async (
    customerAreaId: string,
    currentLinks: string[],
    newLinks: CategoryItem[]
  ) => {
    const currentLinkIds = currentLinks
      .map(name => superAdminCategories.find(cat => cat.label === name)?.value)
      .filter(Boolean) as string[];

    const newLinkIds = newLinks.map(cat => cat.value);

    const toLink = newLinkIds.filter(id => !currentLinkIds.includes(id));
    const toUnlink = currentLinkIds.filter(id => !newLinkIds.includes(id));

    for (const superAdminId of toLink) {
      await serviceAreas.linkCategoryToSuperAdminCategory(superAdminId, customerAreaId);
    }

    for (const superAdminId of toUnlink) {
      await serviceAreas.unlinkCategoryFromSuperAdminCategory(superAdminId, customerAreaId);
    }
  }, [superAdminCategories]);

  const handleSaveNewCategory = async (data: { name: string; accountNumber: string; linkedCategory: CategoryItem[] }) => {
    try {
      const updatedAreas = await serviceAreas.addArea({
        name: data.name,
        accountNumber: data.accountNumber || undefined
      }) as Area[] | null;

      if (updatedAreas && Array.isArray(updatedAreas)) {
        const addedCategory = updatedAreas.find(x => x.name === data.name);

        if (addedCategory) {
          await updateCategoryLinks(addedCategory._id, [], data.linkedCategory);

          NotificationManager.success(t2('notification.success.addArea', 'Yrkeskategori tillagd'));

          setCategories(updatedAreas.map((x: Area) => ({
            label: x.name,
            value: x._id,
            accountNumber: x.accountNumber,
            linkedCategory: x._id === addedCategory._id ? data.linkedCategory.map(cat => cat.label) : []
          })));
        }

        handleCloseAddModal();
      }
    } catch (error) {
      console.error('Error adding category:', error);
      NotificationManager.error(t2('notification.error.addArea', 'Kunde inte lägga till yrkeskategori'));
    }
  };

  const handleCloseAddModal = () => {
    setAddNewAreaModal(false);
    setAddModalKey(prev => prev + 1);
  };

  const cancelEdit = useCallback(() => {
    if (nameCheckTimer.current) clearTimeout(nameCheckTimer.current);
    editDraftRef.current = null;
    nameErrorRef.current = '';
    setEditingRowId(null);
    setSavingRowId(null);
  }, []);

  const patchDraft = useCallback((patch: Partial<EditDraft>) => {
    if (!editDraftRef.current) return;
    editDraftRef.current = { ...editDraftRef.current, ...patch };
    forceDraftRender();
  }, []);

  const nameError = useCallback((name: string, selfId: string) =>
    !name.trim()
      ? t('edit.nameRequired', 'Kategorinamn krävs')
      : categories.some(c => c.value !== selfId &&
        c.label.trim().toLowerCase() === name.trim().toLowerCase())
        ? t2('addArea.tooltip.areaAlreadyExists', 'Kategori finns redan')
        : '', [categories, t, t2]);

  const NAME_CHECK_DEBOUNCE_MS = 500;
  const scheduleNameCheck = useCallback((selfId: string) => {
    if (nameCheckTimer.current) clearTimeout(nameCheckTimer.current);
    nameCheckTimer.current = setTimeout(() => {
      const draft = editDraftRef.current;
      nameErrorRef.current = draft ? nameError(draft.name, selfId) : '';
      forceDraftRender();
    }, NAME_CHECK_DEBOUNCE_MS);
  }, [nameError]);

  const startEdit = useCallback((row: CategoryItem) => {
    if (nameCheckTimer.current) clearTimeout(nameCheckTimer.current);
    editDraftRef.current = {
      name: row.label,
      accountNumber: row.accountNumber || '',
      linkedCategory: row.linkedCategory
        ? superAdminCategories.filter(cat => row.linkedCategory?.includes(cat.label))
        : []
    };
    nameErrorRef.current = '';
    setEditingRowId(row.value);
  }, [superAdminCategories]);

  // Clear any pending debounce timer on unmount.
  useEffect(() => () => {
    if (nameCheckTimer.current) clearTimeout(nameCheckTimer.current);
  }, []);

  const saveEdit = useCallback(async (row: CategoryItem) => {
    const draft = editDraftRef.current;
    if (!draft || nameError(draft.name, row.value)) return;

    setSavingRowId(row.value);
    try {
      await serviceAreas.updateAreaDetails(row.value, {
        name: draft.name.trim(),
        accountNumber: draft.accountNumber.trim() || undefined
      });

      await updateCategoryLinks(row.value, row.linkedCategory || [], draft.linkedCategory);

      setCategories(prev => prev.map(p => p.value === row.value
        ? {
          ...p,
          label: draft.name.trim(),
          accountNumber: draft.accountNumber.trim() || undefined,
          linkedCategory: draft.linkedCategory.map(cat => cat.label)
        }
        : p));

      NotificationManager.success(t('notification.success.edit', 'Kategori uppdaterad'));
      cancelEdit();
    } catch (error) {
      console.error('Error updating category:', error);
      NotificationManager.error(t('notification.error.edit', 'Kunde inte uppdatera kategori'));
    } finally {
      setSavingRowId(null);
    }
  }, [nameError, updateCategoryLinks, cancelEdit, t]);

  const handleDelete = useCallback(async (category: CategoryItem) => {
    if (!window.confirm(t('delete.confirm', 'Är du säker på att du vill ta bort {{name}}?', { name: category.label }))) {
      return;
    }

    try {
      await serviceAreas.deleteArea(category.value);
      setCategories(prev => prev.filter(p => p.value !== category.value));
      NotificationManager.success(t('notification.success.delete', 'Kategori borttagen'));
    } catch (error) {
      console.error('Error deleting category:', error);
      NotificationManager.error(t('notification.error.delete', 'Kunde inte ta bort kategori'));
    }
  }, [t]);

  const url = appState._getCurrentOrgUrl();
  const isSuperAdminOrganization = appState.isSuperAdmin() && appState.getCurrentOrgSlug() === 'superadmin';

  const filteredCategories = useMemo(() => categories
    .filter((category) => {
      if (!searchTerm.trim()) return true;
      const searchLower = searchTerm.toLowerCase();
      return (
        category.label.toLowerCase().includes(searchLower) ||
        category.accountNumber?.toLowerCase().includes(searchLower) ||
        category.linkedCategory?.some(link => link.toLowerCase().includes(searchLower))
      );
    })
    .sort((a, b) => a.label.localeCompare(b.label)), [categories, searchTerm]);

  const handleRowClick = (_event?: React.MouseEvent, rowData?: CategoryItem) => {
    if (editingRowId !== null) return; // don't navigate while a row is being edited
    if (isSuperAdminOrganization && rowData?.slug) {
      history.push(`${url}/categories/${rowData.slug}`);
    }
  };

  const tableColumns = useMemo(() => [
    {
      title: t('columns.name', 'Namn'),
      field: 'label',
      render: (rowData: CategoryItem) => {
        const draft = editDraftRef.current;
        if (rowData.value !== editingRowId || !draft) {
          return (
            <span style={{ fontWeight: 600, color: 'var(--gray-900)' }}>
              {rowData.label}
            </span>
          );
        }
        const err = nameErrorRef.current;
        return (
          <div onClick={(e) => e.stopPropagation()}>
            <Field
              value={draft.name}
              onChange={(value) => { patchDraft({ name: value }); scheduleNameCheck(rowData.value); }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') saveEdit(rowData);
                if (e.key === 'Escape') cancelEdit();
              }}
              errorMessage={err || undefined}
              required
            />
          </div>
        );
      },
      customSort: (a: CategoryItem, b: CategoryItem) => a.label.localeCompare(b.label),
    },
    {
      title: t('columns.accountNumber', 'Kontonummer'),
      field: 'accountNumber',
      render: (rowData: CategoryItem) => {
        const draft = editDraftRef.current;
        if (rowData.value !== editingRowId || !draft) {
          return <span style={{ color: 'var(--gray-700)' }}>{rowData.accountNumber || '–'}</span>;
        }
        return (
          <div onClick={(e) => e.stopPropagation()}>
            <Field
              value={draft.accountNumber}
              onChange={(value) => patchDraft({ accountNumber: value })}
              onKeyDown={(e) => {
                if (e.key === 'Enter') saveEdit(rowData);
                if (e.key === 'Escape') cancelEdit();
              }}
              placeholder={t('edit.accountNumberPlaceholder', 'T.ex. 1234')}
            />
          </div>
        );
      },
      customSort: (a: CategoryItem, b: CategoryItem, _: any, order: 'asc' | 'desc') => {
        const hasA = a.accountNumber?.trim();
        const hasB = b.accountNumber?.trim();

        if (!hasA && !hasB) return 0;

        if (order === 'asc') {
          if (!hasA) return 1;
          if (!hasB) return -1;
        } else {
          if (!hasA) return -1;
          if (!hasB) return 1;
        }

        return parseFloat(a.accountNumber!) - parseFloat(b.accountNumber!);
      },
    },
    {
      title: t('columns.linkedCategories', 'Kopplade globala kategorier'),
      field: 'linkedCategory',
      render: (rowData: CategoryItem) => {
        const draft = editDraftRef.current;
        if (rowData.value !== editingRowId || !draft) {
          const links = rowData.linkedCategory ?? [];
          if (!links.length) {
            return <span style={{ color: 'var(--gray-700)' }}>–</span>;
          }
          const linkedText = links.join(', ');
          const textStyle: React.CSSProperties = {
            color: 'var(--gray-700)',
            display: 'inline-block',
            maxWidth: '320px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            verticalAlign: 'bottom',
          };
          // A single category can't overflow the list meaningfully — no tooltip.
          if (links.length === 1) {
            return <span style={textStyle}>{linkedText}</span>;
          }
          return (
            <TooltipContainer
              renderReferenceComponent={(className, ref) => (
                <span ref={ref} className={className} style={textStyle}>
                  {linkedText}
                </span>
              )}
            >
              <div style={{ maxWidth: '360px' }}>{linkedText}</div>
            </TooltipContainer>
          );
        }
        return (
          <div onClick={(e) => e.stopPropagation()} style={{ minWidth: '220px', maxWidth: '360px' }}>
            <Selector
              isMulti
              isSearchable
              isOverflow
              options={superAdminCategories}
              value={draft.linkedCategory}
              onChange={(selected) => patchDraft({ linkedCategory: (selected as CategoryItem[]) || [] })}
              placeholder={t('edit.selectPlaceholder', 'Välj')}
              style={{ width: '100%' }}
            />
          </div>
        );
      },
      customSort: (a: CategoryItem, b: CategoryItem, _: any, order: 'asc' | 'desc') => {
        const lengthA = a.linkedCategory?.length ?? 0;
        const lengthB = b.linkedCategory?.length ?? 0;
        const hasA = lengthA > 0;
        const hasB = lengthB > 0;

        if (!hasA && !hasB) return 0;

        if (order === 'asc') {
          if (!hasA) return 1;
          if (!hasB) return -1;
        } else {
          if (!hasA) return -1;
          if (!hasB) return 1;
        }

        return lengthA - lengthB;
      },
      hidden: isSuperAdminOrganization,
    },
    {
      title: '',
      field: 'actions',
      sorting: false,
      width: '90px',
      render: (rowData: CategoryItem) => {
        const draft = editDraftRef.current;
        const isEditing = rowData.value === editingRowId;
        const saving = savingRowId === rowData.value;

        if (isEditing && draft) {
          // Empty-name disables instantly (cheap); the dup check is debounced.
          const disableSave = !draft.name.trim() || !!nameErrorRef.current || saving;
          return (
            <div
              style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}
              onClick={(e) => e.stopPropagation()}
            >
              <BtButton
                size="xxs"
                onClick={() => saveEdit(rowData)}
                disabled={disableSave}
                loaderShow={saving}
              >
                {saving ? t('edit.saving', 'Sparar...') : t('edit.save', 'Spara')}
              </BtButton>
              <BtButton color="white" size="xxs" onClick={cancelEdit} disabled={saving}>
                {t('edit.cancel', 'Avbryt')}
              </BtButton>
            </div>
          );
        }

        const isAnotherRowEditing = editingRowId !== null;

        return (
          <div
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <span title={t('menu.edit', 'Redigera')}>
              <BtButton color="white" size="xxs" disabled={isAnotherRowEditing} onClick={() => startEdit(rowData)}>
                <EditIcon style={{ width: 14, height: 14, fill: 'var(--gray-700)' }} />
              </BtButton>
            </span>
            <span title={t('menu.delete', 'Ta bort')}>
              <BtButton color="white" size="xxs" disabled={isAnotherRowEditing} onClick={() => handleDelete(rowData)}>
                <TrashIcon size="14px" color="var(--gray-700)" />
              </BtButton>
            </span>
          </div>
        );
      }
    }
  ], [editingRowId, savingRowId, superAdminCategories, isSuperAdminOrganization, t, startEdit, cancelEdit, patchDraft, saveEdit, scheduleNameCheck, handleDelete]);

  return <div>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
      <p className={styles.intro}>
        {t('intro', 'Här kan du byta namn på eller ta bort yrkeskategorier. Namnbyten påverkar både befintliga upphandlingar och leverantörer. Borttagna kategorier försvinner från leverantörer men befintliga upphandlingar påverkas inte.')}
      </p>
    </div>

    <div style={{ marginTop: '24px' }}>
      <SearchBarWithActions
        value={searchTerm}
        onChange={setSearchTerm}
        style={{ marginBottom: '16px' }}
      />
    </div>

    <div style={{ marginTop: '24px' }} className={styles.row}>
      <DataTable
        isLoading={loading}
        columns={tableColumns}
        data={filteredCategories}
        defaultOrderBy={0}
        defaultOrderDirection="asc"
        defaultOrderByField="label"
        onRowClick={isSuperAdminOrganization ? handleRowClick : undefined}
      />
    </div>

    <Modal
      key={`addCategory${addModalKey}`}
      title={t2('addArea.placeholder.areaName', 'Lägg till yrkeskategori')}
      show={addNewAreaModal}
      setShow={handleCloseAddModal}
      hasCloseButton
    >
      <AddAreaForm
        categories={categories}
        superAdminCategories={superAdminCategories}
        loading={loading}
        isSuperAdminOrganization={isSuperAdminOrganization}
        onSave={handleSaveNewCategory}
        onClose={handleCloseAddModal}
      />
    </Modal>
  </div>;
}

export default AppEditCategories;
